import React, { useState } from "react";
import { useTranslation } from "../lib/i18n";
import { Users, Droplet, Zap, Star, FileText } from "lucide-react";

type Bus = {
  id?: string;
  name: string;
  images?: string[];
  planImage?: string;
  seats?: string;
  trunk?: string;
  fuel?: string;
  advantages?: string[];
};

export default function BusDetails({ bus }: { bus: Bus }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(0);
  const [modalSrc, setModalSrc] = useState<string | null>(null);
  const currentImage = (bus.images && (bus.images[selected] ?? bus.images[0])) ?? "";

  const openModal = (src: string) => setModalSrc(src);
  const closeModal = () => setModalSrc(null);

  return (
    <div className="max-w-[1100px] mx-auto p-8">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-6">
        <div>
          <img
            src={currentImage}
            alt={bus.name}
            className="w-full h-[480px] object-cover cursor-zoom-in border border-gray-200"
            onClick={() => openModal(currentImage)}
          />

          <div className="flex gap-2 mt-3 overflow-x-auto">
            {(bus.images ?? []).map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`${bus.name} ${i + 1}`}
                onClick={() => setSelected(i)}
                className={`w-24 h-16 object-cover cursor-pointer flex-shrink-0 ${i === selected ? "border-2 border-blue-800" : "border border-gray-200"}`}
              />
            ))}
          </div>
        </div>

        <aside className="pt-2">
          <h1 className="mt-0 text-lg font-semibold">{bus.name}</h1>

          <ul className="list-none p-0 text-gray-700">
            <li className="mb-2 flex items-center gap-2">
              <Users size={18} className="text-blue-800" />
              <span className="font-semibold">{t("seatsLabel")}:</span>
              <span className="ml-1">{bus.seats ?? ""}</span>
            </li>
            {bus.trunk && (
              <li className="mb-2 flex items-center gap-2">
                <Droplet size={18} className="text-teal-500" />
                <span className="font-semibold">{t("tankLabel")}:</span>
                <span className="ml-1">{bus.trunk}</span>
              </li>
            )}
            {bus.fuel && (
              <li className="mb-2 flex items-center gap-2">
                <Zap size={18} className="text-orange-500" />
                <span className="font-semibold">{t("fuelLabel")}:</span>
                <span className="ml-1">{bus.fuel}</span>
              </li>
            )}
          </ul>

          {bus.advantages && bus.advantages.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center gap-2">
                <Star size={18} className="text-blue-600" />
                <strong>{t("advantagesLabel")}</strong>
              </div>
              <ul className="mt-2 text-gray-700 list-disc list-inside">
                {bus.advantages.map((a, idx) => (
                  <li key={idx}>{t(a)}</li>
                ))}
              </ul>
            </div>
          )}

          {bus.planImage && (
            <button
              onClick={() => openModal(bus.planImage!)}
              className="mt-6 bg-red-600 hover:bg-red-700 text-white py-3 px-4 rounded-md flex items-center gap-2"
            >
              <FileText size={16} />
              <span>{t("busPlan")}</span>
            </button>
          )}
        </aside>
      </div>

      {modalSrc && (
        <div role="dialog" aria-modal="true" onClick={closeModal} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <img src={modalSrc} alt="preview" onClick={(e) => e.stopPropagation()} className="max-w-[92%] max-h-[92%] shadow-2xl object-contain" />
        </div>
      )}

      <p className="mt-10 text-center text-sm text-sky-700 max-w-3xl mx-auto">{t("contactPrompt")}</p>
    </div>
  );
}
